/* =============================================================================
 * Conversao de valores vindos da API.
 *
 * Substitui preenchido/empty/toBool/toDate/toDecimal do LibGeral, mas como
 * IMPORT NOMEADO - nao como global no window. A semantica de cada um e' a do
 * backend e por isso e' mantida literal (ver comentarios).
 * ========================================================================== */

/** Vazio = null, undefined, string em branco, array vazio ou NaN. Zero e false NAO sao vazios. */
export function vazio(valor) {
    if (valor === null || valor === undefined) return true;
    if (typeof valor === 'string') return valor.trim() === '';
    if (typeof valor === 'number') return Number.isNaN(valor);
    if (Array.isArray(valor)) return valor.length === 0;
    return false;
}

export function preenchido(valor) {
    return !vazio(valor);
}

/**
 * Booleano na convencao do backend: 'S'/'N' das colunas Oracle, alem de
 * true/false, 1/0 e 'TRUE'/'ON'. Manter literal - e' convencao de dados, nao
 * gosto pessoal.
 */
export function paraBooleano(valor) {
    if (typeof valor === 'boolean') return valor;
    if (typeof valor === 'number') return valor !== 0;
    if (vazio(valor)) return false;
    const t = String(valor).trim().toUpperCase();
    return t === 'S' || t === 'TRUE' || t === 'ON' || t === '1' || t === 'SIM' || t === 'Y';
}

/**
 * Inteiro para ids e parametros de rota. Separado de paraNumero de proposito:
 * o toDecimal generico do LibGeral aceitava '1.234,56' e virava fonte de bug
 * silencioso quando aplicado a um id.
 */
export function paraInteiro(valor) {
    if (typeof valor === 'number') return Number.isFinite(valor) ? Math.trunc(valor) : 0;
    if (vazio(valor)) return 0;
    const n = parseInt(String(valor).trim(), 10);
    return Number.isNaN(n) ? 0 : n;
}

/** Decimal vindo da API. Tolera o formato pt-BR ('1.234,56') porque algumas colunas chegam formatadas. */
export function paraNumero(valor) {
    if (typeof valor === 'number') return Number.isFinite(valor) ? valor : 0;
    if (vazio(valor)) return 0;

    let t = String(valor).trim();
    const temVirgula = t.includes(',');
    const temPonto = t.includes('.');

    if (temVirgula && temPonto) {
        //'1.234,56' -> ponto e' separador de milhar
        t = t.replaceAll('.', '').replace(',', '.');
    } else if (temVirgula) {
        t = t.replace(',', '.');
    }

    const n = Number(t);
    return Number.isNaN(n) ? 0 : n;
}

const PADRAO_DATA = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:\.(\d+))?)?/;

/**
 * IMPORTANTE: o backend envia hora LOCAL com um sufixo 'Z' FALSO
 * (ver 2DExtend.js:250-255 do iPrisma, que sobrescreve Date.prototype.toJSON).
 * '2026-08-15T20:00:00.0Z' significa 20h no horario de Brasilia, nao em UTC.
 *
 * Por isso NAO usar new Date(string): o parser nativo respeita o 'Z' e desloca
 * o horario - um jogo das 20h apareceria as 17h. Parseamos os componentes e
 * montamos a data no fuso local.
 */
export function paraData(valor) {
    if (valor instanceof Date) return Number.isNaN(valor.getTime()) ? null : valor;
    if (vazio(valor)) return null;

    const m = PADRAO_DATA.exec(String(valor).trim());
    if (!m) return null;

    const [, ano, mes, dia, hora, minuto, segundo, milissegundo] = m;
    const anoNumero = paraInteiro(ano);

    //O backend usa 01/01/0001 e 31/12/9999 como "sem data". A checagem e' feita
    //no ano do TEXTO, nao no do objeto Date: new Date(1, ...) vira 1901, porque
    //o construtor trata anos de 0 a 99 como 1900 + ano.
    if (anoNumero <= 1 || anoNumero >= 9999) return null;

    const d = new Date(anoNumero, paraInteiro(mes) - 1, paraInteiro(dia), paraInteiro(hora), paraInteiro(minuto), paraInteiro(segundo), paraInteiro((milissegundo ?? '0').padEnd(3, '0').slice(0, 3)));

    return Number.isNaN(d.getTime()) ? null : d;
}

export function dataValida(valor) {
    return paraData(valor) !== null;
}

/** null/undefined viram [] - varias funcionalidades devolvem null em vez de lista vazia. */
export function paraLista(valor) {
    if (Array.isArray(valor)) return valor;
    return vazio(valor) ? [] : [valor];
}

export function paraTexto(valor) {
    return vazio(valor) ? '' : String(valor).trim();
}
